import React, { useState, useEffect, useRef } from 'react'
import { Button, ButtonGroup, Link } from '@mui/material/'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import * as ROUTES from '../routes'
import { getBannerProperties, getFeaturedProperties, getFeaturedLand } from '../backend/api'
import setStateAsync from '../backend/setStateAsync'
import OverlayImage from '../components/overlayImage'
import FeaturedProperties from '../components/featuredProperties'
import HomeIcon from '@mui/icons-material/Home'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import LoadingScreen from '../components/loadingComponent'
import ErrorScreen from '../components/errorScreen'
import Card from '../components/Card'
import bgImage from '../resources/bg-image.webp'
import heroImage from '../resources/choices1.webp'
import ctaBgImage from '../resources/choices2.webp'


export default function Home(props) {

    const [error, toggleError] = useState(false)
    const [loading, toggleLoading] = useState(true)
    const [listingState, setListingState] = useState(0)
    const [bannerProperties, setBannerProperties] = useState([])
    const [featuredLand, setFeaturedLand] = useState([])
    const [featuredProperties, setFeaturedProperties] = useState([])
    const mounted = useRef(true)
    const navigate = useNavigate()

    useEffect(() => {
        getData()
        return () => mounted.current = false
    }, [])

    async function getData() {
        try {
            setStateAsync(true, toggleLoading, mounted.current)
            const [bannerData, propertiesData, landData] = await Promise.all([
                getBannerProperties(),
                getFeaturedProperties(),
                getFeaturedLand()
            ])
            setStateAsync(bannerData, setBannerProperties, mounted.current)
            setStateAsync(propertiesData, setFeaturedProperties, mounted.current)
            setStateAsync(landData, setFeaturedLand, mounted.current)
            setStateAsync(false, toggleError, mounted.current)
        } catch (e) {
            setStateAsync(true, toggleError, mounted.current)
        } finally {
            setStateAsync(false, toggleLoading, mounted.current)
        }
    }

    if (loading)
        return (<LoadingScreen styles={{ height: 500 }} />)
    if (error)
        return (<ErrorScreen errorText='Error contacting server' buttonText='Try Again' onClick={() => getData()} />)

    return (
        <div style={{ pb: 2, bgcolor: 'paper', height: '100%' }}>

            {/* ── Hero Section ── */}
            <div style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'stretch',
                justifyContent: 'space-between',
                height: '100vh',
                overflow: 'hidden',
                backgroundColor: '#f9f9f9',
            }}>
                {/* Left Text */}
                <div style={{
                    maxWidth: '480px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    padding: '60px 80px',
                }}>
                    <span style={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        letterSpacing: '0.15em',
                        textTransform: 'uppercase',
                        border: '1px solid #333',
                        padding: '4px 12px',
                        borderRadius: '20px',
                        marginBottom: '24px',
                        display: 'inline-block'
                    }}>
                        Choice Real Estate
                    </span>
                    <h1 style={{
                        fontSize: 'clamp(2.5rem, 5vw, 4rem)',
                        fontWeight: 800,
                        lineHeight: 1.1,
                        margin: '16px 0 24px',
                        color: '#111'
                    }}>
                        Caribbean Living, Elevated.
                    </h1>
                    <p style={{
                        fontSize: '1rem',
                        color: '#555',
                        lineHeight: 1.7,
                        marginBottom: '36px',
                        maxWidth: '380px'
                    }}>
                        Discover exceptional properties and meticulous management services in Dominica, the nature island of the Caribbean.
                    </p>
                    <Button
                        variant='contained'
                        size='large'
                        onClick={() => navigate(`/${ROUTES.PROPERTIES}`)}
                        sx={{
                            backgroundColor: '#1b4332',
                            borderRadius: '4px',
                            px: 4,
                            py: 1.5,
                            fontSize: '0.95rem',
                            textTransform: 'none',
                            alignSelf: 'flex-start',
                            '&:hover': { backgroundColor: '#2d6a4f' }
                        }}
                    >
                        Browse Properties →
                    </Button>
                </div>

                {/* Right Image */}
                <div style={{
                    flex: 1,
                    height: '100%',
                    overflow: 'hidden',
                    flexShrink: 0,
                }}>
                    <img
                        src={heroImage}
                        alt='Caribbean Real Estate'
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            objectPosition: 'center',
                            display: 'block',
                        }}
                    />
                </div>
            </div>

            {/* ── Carousel ── */}
            <OverlayImage
                style={{ height: 400 }}
                onClick={(slug) => navigate(`/${ROUTES.PROPERTIES}/${slug}`)}
                items={bannerProperties}
            />

            {/* ── Featured Listings ── */}
            <Card style={{ width: '100%', display: 'flex', flexDirection: 'column', backgroundColor: 'background.lightSecondary', padding: 2 }}>
                <HomeIcon sx={{ alignSelf: 'center', justifySelf: 'center', fontSize: '60px' }} />
                <h3 style={{ textAlign: 'center', marginBottom: 3 }}>Featured Listings</h3>
                <ButtonGroup sx={{ alignSelf: 'center', mb: 5 }}>
                    <Button variant={listingState === 0 ? 'contained' : 'outlined'} onClick={() => setListingState(0)}>Properties</Button>
                    <Button variant={listingState === 1 ? 'contained' : 'outlined'} onClick={() => setListingState(1)}>Land</Button>
                </ButtonGroup>

                {
                    listingState === 0 ?
                        <FeaturedProperties properties={featuredProperties} /> :
                        <FeaturedProperties properties={featuredLand} />
                }
                {(featuredProperties?.length > 0 || featuredLand.length > 0) &&
                    <Link component={RouterLink} to={ROUTES.PROPERTIES} sx={{ textAlign: 'center', my: 3, cursor: 'pointer' }}>
                        See All Listings
                    </Link>
                }
            </Card>

            {/* ── List Your Property ── */}
            <div style={{
                width: '100%',
                backgroundImage: `url(${ctaBgImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '80px 20px',
                minHeight: '400px'
            }}>
                <Card style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: '40px',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    maxWidth: '600px',
                    borderRadius: '8px'
                }}>
                    <AttachMoneyIcon sx={{ alignSelf: 'center', justifySelf: 'center', fontSize: '60px' }} />
                    <h3 style={{ textAlign: 'center', margin: 0, marginBottom: 3 }}>List your property with us</h3>
                    <p style={{ maxWidth: '400px', textAlign: 'justify' }}>
                        Interested in selling or listing your property? Contact us today and we will get back to you with next steps.
                    </p>
                    <Link component={RouterLink} to={ROUTES.CONTACT} underline='none' alignSelf='center'>
                        <Button>Contact Us</Button>
                    </Link>
                </Card>
            </div>

        </div>
    )
}